import { useState, useEffect } from 'react';
import LoadsTrendChart from './overview/LoadsTrendChart.jsx';
import { useApi } from '../api.js';
import { ClickableValue } from './DrillDown.jsx';

const num = (n) => (n ?? 0).toLocaleString();
const money = (n) => '$' + Math.round(n ?? 0).toLocaleString();
const moneyK = (n) => '$' + ((n ?? 0) / 1000).toFixed(0) + 'K';
const moneyM = (n) => '$' + ((n ?? 0) / 1000000).toFixed(2) + 'M';

// Options render as three <optgroup>s. 'server' options pass their value
// straight to /api/overview?period=. 'preset' options compute a concrete
// start/end locally (presetRange below) and go through the custom-range
// API path with a friendly label — the server compares any custom range
// to the immediately-prior window of the same length.
const PERIOD_GROUPS = [
  { group: 'To date', options: [
    { value: 'p_today',      label: 'Today',                preset: true },
    { value: 'p_wtd',        label: 'This week to date',    preset: true },
    { value: 'p_mtd',        label: 'This month to date',   preset: true },
    { value: 'p_qtd',        label: 'This quarter to date', preset: true },
    { value: 'p_ytd',        label: 'Calendar year to date', preset: true },
    { value: 'fy',           label: 'Fiscal year to date (vs same period prior FY)' }
  ]},
  { group: 'Completed periods', options: [
    { value: 'yesterday',    label: 'Prior day (vs prior-year avg day)' },
    { value: 'week',         label: 'Prior week (vs prior-year avg week)' },
    { value: 'month',        label: 'Prior month (vs the month before)' },
    { value: 'quarter',      label: 'Prior quarter (vs the quarter before)' },
    { value: 'p_lastyear',   label: 'Last calendar year',   preset: true }
  ]},
  { group: 'Rolling windows', options: [
    { value: 'p_7d',         label: 'Last 7 days',   preset: true },
    { value: 'p_30d',        label: 'Last 30 days',  preset: true },
    { value: 'p_60d',        label: 'Last 60 days',  preset: true },
    { value: 'p_90d',        label: 'Last 90 days',  preset: true },
    { value: 'trailing12',   label: 'Trailing 12 months (vs months 13–24)' }
  ]},
  { group: 'Pick your own', options: [
    { value: 'custom',       label: 'Custom range (date pickers)' }
  ]}
];
const ALL_OPTIONS = PERIOD_GROUPS.flatMap(g => g.options);

// Concrete {start, end, label} for the preset options. All windows end
// today (or the period's own end) — inclusive ISO dates.
function presetRange(key) {
  const now = new Date();
  const pad = n => String(n).padStart(2, '0');
  const iso = d => d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const back = n => new Date(today.getFullYear(), today.getMonth(), today.getDate() - n);
  switch (key) {
    case 'p_today': return { start: iso(today), end: iso(today), label: 'Today' };
    case 'p_wtd': {
      const dow = (today.getDay() + 6) % 7;               // Monday-start week
      return { start: iso(back(dow)), end: iso(today), label: 'This week to date' };
    }
    case 'p_mtd': return { start: iso(new Date(today.getFullYear(), today.getMonth(), 1)), end: iso(today), label: 'This month to date' };
    case 'p_qtd': {
      const qm = Math.floor(today.getMonth() / 3) * 3;
      return { start: iso(new Date(today.getFullYear(), qm, 1)), end: iso(today), label: 'This quarter to date' };
    }
    case 'p_ytd': return { start: iso(new Date(today.getFullYear(), 0, 1)), end: iso(today), label: 'Calendar year to date' };
    case 'p_lastyear': return {
      start: iso(new Date(today.getFullYear() - 1, 0, 1)),
      end: iso(new Date(today.getFullYear() - 1, 11, 31)),
      label: 'Last calendar year (' + (today.getFullYear() - 1) + ')'
    };
    case 'p_7d':  return { start: iso(back(6)),  end: iso(today), label: 'Last 7 days' };
    case 'p_30d': return { start: iso(back(29)), end: iso(today), label: 'Last 30 days' };
    case 'p_60d': return { start: iso(back(59)), end: iso(today), label: 'Last 60 days' };
    case 'p_90d': return { start: iso(back(89)), end: iso(today), label: 'Last 90 days' };
    default: return null;
  }
}

// Default the custom range to the current month-to-date so first-open
// isn't blank. User picks whatever they want after.
function defaultCustomRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const pad = n => String(n).padStart(2, '0');
  const iso = d => d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  return { start: iso(start), end: iso(now) };
}

function Delta({ value, suffix = '%', invert = false }) {
  if (value == null || isNaN(value)) return <span className="small">—</span>;
  const up = invert ? value < 0 : value > 0;
  const cls = up ? 'up' : (value === 0 ? '' : 'down');
  const arrow = value > 0 ? '▲' : value < 0 ? '▼' : '';
  const sign = value > 0 ? '+' : '';
  return <span className={cls}>{arrow} {sign}{value.toFixed(1)}{suffix}</span>;
}

function rateTag(rate) {
  if (!rate) return null;
  if (rate >= 600) return <span className="tag tag-prem">${rate}</span>;
  if (rate <= 470) return <span className="tag tag-disc">${rate}</span>;
  return <span className="tag tag-std">${rate}</span>;
}

// Build a drill-down query for the currently selected period
function periodToDrillQuery(periodKey, currentPeriod, priorPeriod) {
  if (!currentPeriod) return {};
  return { start: currentPeriod.start, end: currentPeriod.end, limit: 200 };
}
function priorToDrillQuery(priorPeriod) {
  if (!priorPeriod || priorPeriod.mode !== 'prior-period') return null;
  return { start: priorPeriod.start, end: priorPeriod.end, limit: 200 };
}

export default function Overview() {
  const [period, setPeriod] = useState('week');
  const [customRange, setCustomRange] = useState(defaultCustomRange);
  // Build the API URL. For custom, only fetch when both dates are present
  // and non-empty; otherwise skip (useApi tolerates null path).
  const presetDef = ALL_OPTIONS.find(o => o.value === period && o.preset) ? presetRange(period) : null;
  const apiUrl = presetDef
    ? '/api/overview?period=custom&start=' + presetDef.start + '&end=' + presetDef.end + '&label=' + encodeURIComponent(presetDef.label)
    : period === 'custom'
      ? (customRange.start && customRange.end
          ? '/api/overview?period=custom&start=' + customRange.start + '&end=' + customRange.end
          : null)
      : '/api/overview?period=' + period;
  const { data, error, loading, refresh } = useApi(apiUrl);

  // Outgoing loads for the same period — fetched at the top of Overview
  // so the Loads-Out KPI tile and the OutgoingLoadsAnalytics section
  // share a single API call.
  const cPForFetch = data && data.currentPeriod;
  const outQs = cPForFetch
    ? '/api/loads-outgoing?from=' + cPForFetch.start + '&to=' + cPForFetch.end + '&limit=5000'
    : null;
  const outApi = useApi(outQs);
  const outRows = (outApi.data && outApi.data.rows) || [];
  const outTotals = computeOutgoingTotals(outRows);

  // Drill-down modal state for outgoing loads. Populated by clicking the
  // Loads Out KPI tile (no filter) or any per-hauler / per-destination
  // tile in the OutgoingLoadsAnalytics section (with a filter).
  const [outDrill, setOutDrill] = useState(null);   // { title, filter } | null

  // Custom with incomplete range: apiUrl is null so useApi hasn't fetched.
  // Show the pickers instead of a loading spinner.
  const customIncomplete = period === 'custom' && (!customRange.start || !customRange.end);
  if (customIncomplete) {
    return (
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '1rem', flexWrap: 'wrap' }}>
        <strong>Business Snapshot</strong>
        <span className="small" style={{ color: '#666', marginRight: '0.5rem' }}>— pick a custom date range:</span>
        <select value={period} onChange={(e) => setPeriod(e.target.value)} className="refresh-btn" style={{ minWidth: 220 }}>
          {PERIOD_GROUPS.map(g => (
            <optgroup key={g.group} label={g.group}>
              {g.options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </optgroup>
          ))}
        </select>
        <label className="small" style={{ color: '#666' }}>From</label>
        <input type="date" value={customRange.start}
               onChange={e => setCustomRange(r => ({ ...r, start: e.target.value }))}
               className="refresh-btn" style={{ padding: '4px 6px' }} />
        <label className="small" style={{ color: '#666' }}>To</label>
        <input type="date" value={customRange.end}
               onChange={e => setCustomRange(r => ({ ...r, end: e.target.value }))}
               className="refresh-btn" style={{ padding: '4px 6px' }} />
      </div>
    );
  }
  if (loading) return <div className="loading">Loading business snapshot…</div>;
  if (error)   return <div className="error">Error: {error}</div>;
  if (!data)   return null;

  const A = data.current;
  const B = data.prior;
  const D = data.deltas;
  const cP = data.currentPeriod;
  const pP = data.priorPeriod;
  const isAvgMode = pP && (pP.mode === 'avg-day' || pP.mode === 'avg-week');
  const priorLabel = isAvgMode ? (pP.mode === 'avg-day' ? 'prior-yr avg day' : 'prior-yr avg week') : 'prior period';

  const qA = periodToDrillQuery(data.periodKey, cP, pP);
  const qB = priorToDrillQuery(pP);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.4rem' }}>
        <div>
          <h2 style={{ margin: 0 }}>Business Snapshot — {data.periodLabel}</h2>
          <div className="small" style={{ marginTop: '0.2rem' }}>
            Current: <strong>{cP.label}</strong>
            {' '}· Compared to <strong>{pP.label}</strong>.
            {' '}Click any number to see the underlying loads.
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={period} onChange={(e) => setPeriod(e.target.value)} className="refresh-btn" style={{ minWidth: 280 }}>
            {PERIOD_GROUPS.map(g => (
              <optgroup key={g.group} label={g.group}>
                {g.options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </optgroup>
            ))}
          </select>
          {period === 'custom' && (
            <>
              <label className="small" style={{ color: '#666' }}>From</label>
              <input type="date" value={customRange.start}
                     onChange={e => setCustomRange(r => ({ ...r, start: e.target.value }))}
                     className="refresh-btn" style={{ padding: '4px 6px' }} />
              <label className="small" style={{ color: '#666' }}>To</label>
              <input type="date" value={customRange.end}
                     onChange={e => setCustomRange(r => ({ ...r, end: e.target.value }))}
                     className="refresh-btn" style={{ padding: '4px 6px' }} />
            </>
          )}
          <button className="refresh-btn" onClick={refresh}>Refresh</button>
        </div>
      </div>

      <div className="kpi-grid">
        <KpiCard lbl="Loads"
          valQuery={qA} val={num(A.tips)}
          delta={<Delta value={D.tips} />}
          vs={<>vs {qB
            ? <ClickableValue query={qB} title="Loads — prior period">{num(B?.tips ?? 0)}</ClickableValue>
            : (B ? num(B.tips) : '—')} {priorLabel}</>}
          title={'Loads — ' + data.periodLabel} />
        <KpiCard lbl="Volume m³"
          valQuery={qA} val={num(A.volumeM3)}
          delta={<Delta value={D.volumeM3} />}
          vs={<>vs {qB
            ? <ClickableValue query={qB} title="Volume — prior period">{num(B?.volumeM3 ?? 0)}</ClickableValue>
            : (B ? num(B.volumeM3) : '—')} {priorLabel}</>}
          title={'Volume — ' + data.periodLabel} />
        <KpiCard lbl="Est. Revenue"
          valQuery={qA} val={A.revenue >= 1000000 ? moneyM(A.revenue) : moneyK(A.revenue)}
          delta={<Delta value={D.revenue} />}
          vs={<>vs {qB
            ? <ClickableValue query={qB} title="Revenue — prior period">{B?.revenue >= 1000000 ? moneyM(B.revenue) : moneyK(B?.revenue ?? 0)}</ClickableValue>
            : (B ? (B.revenue >= 1000000 ? moneyM(B.revenue) : moneyK(B.revenue)) : '—')} {priorLabel}</>}
          title={'Revenue — ' + data.periodLabel} />
        <KpiCard lbl="Avg Rate / Load"
          valQuery={qA} val={money(A.avgRate)}
          delta={<Delta value={D.avgRate} suffix="" />}
          vs={<>vs {B ? money(B.avgRate) : '—'} {priorLabel}</>}
          title={'Avg rate per load — ' + data.periodLabel} />
        {(() => {
          const bg = data.badger || {};
          const bgDelta = (bg.prior != null) ? (bg.current - bg.prior) : null;
          return (
            <KpiCard lbl="Badger"
              valQuery={{ ...qA, customer: 'Badger' }} val={num(bg.current || 0)}
              delta={bgDelta == null
                ? <span className="small">—</span>
                : <Delta value={bgDelta} suffix="" />}
              vs={<span className="small" style={{ display:'inline-flex', flexWrap:'wrap', gap:'0.4rem', color:'#64748b' }}>
                <span>Week: <strong>{num(bg.week || 0)}</strong></span>
                <span>· Month: <strong>{num(bg.month || 0)}</strong></span>
                <span>· Prior mo: <strong>{num(bg.priorMonth || 0)}</strong></span>
                <span>· Wk avg: <strong>{Number(bg.weeklyAvg || 0).toFixed(1)}</strong></span>
              </span>}
              title={'Badger loads — ' + data.periodLabel} />
          );
        })()}
        <KpiCard lbl="Loads out"
          valQuery={null}
          val={outApi.loading
            ? '…'
            : <span className="clickable"
                    onClick={() => setOutDrill({ title: 'Outgoing loads — ' + data.periodLabel, filter: null })}
                    role="button" tabIndex={0}
                    onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setOutDrill({ title: 'Outgoing loads — ' + data.periodLabel, filter: null })}>
                {num(outTotals.totalLoads)}
              </span>}
          delta={<span className="small" style={{ color: '#64748b' }}>
            {outTotals.haulerCount} trucking compan{outTotals.haulerCount === 1 ? 'y' : 'ies'}
          </span>}
          vs={<span className="small" style={{ color: '#94a3b8' }}>
            {outApi.error ? 'error loading outgoing loads' : ('for ' + data.periodLabel)}
          </span>}
          title={'Outgoing loads — ' + data.periodLabel} />
        <KpiCard lbl="Loads / Working Day*"
          valQuery={qA} val={A.tipsPerDay.toFixed(1)}
          delta={<Delta value={D.tipsPerDay} />}
          vs={<>vs {B ? B.tipsPerDay.toFixed(1) : '—'} {priorLabel}</>}
          title={'Loads per working day (Mon-Fri) — ' + data.periodLabel} />
        <KpiCard lbl="Revenue / Working Day*"
          valQuery={qA} val={money(A.revPerDay)}
          delta={<Delta value={D.revPerDay} />}
          vs={<>vs {B ? money(B.revPerDay) : '—'} {priorLabel}</>}
          title={'Revenue per working day (Mon-Fri) — ' + data.periodLabel} />
      </div>

      <OutgoingLoadsAnalytics from={isoOf(cP.start)} to={isoOf(cP.end)} periodLabel={data.periodLabel}
                              onDrill={setOutDrill} />

      {outDrill && (
        <OutgoingDrillDown rows={outRows}
                          from={isoOf(cP.start)} to={isoOf(cP.end)}
                          title={outDrill.title} filter={outDrill.filter}
                          onClose={() => setOutDrill(null)} />
      )}

      <div className="panel-row">
        <div className="panel">
          <h3>Top 10 customers — {data.periodLabel}</h3>
          <table>
            <thead>
              <tr>
                <th>Customer</th>
                {!isAvgMode && <th className="num">Prior loads</th>}
                <th className="num">Loads</th>
                {!isAvgMode && <th className="num">Δ</th>}
                <th className="num">Revenue</th>
                <th>Rate</th>
              </tr>
            </thead>
            <tbody>
              {data.topCustomers.map(c => (
                <tr key={c.customer}>
                  <td>{c.customer}</td>
                  {!isAvgMode && (
                    <td className="num">
                      {qB
                        ? <ClickableValue query={{ ...qB, customer: c.customer }} title={c.customer + ' — prior period'}>{num(c.priorTips ?? 0)}</ClickableValue>
                        : num(c.priorTips ?? 0)}
                    </td>
                  )}
                  <td className="num">
                    <ClickableValue query={{ ...qA, customer: c.customer }} title={c.customer + ' — ' + data.periodLabel}>{num(c.currTips)}</ClickableValue>
                  </td>
                  {!isAvgMode && <td className="num"><Delta value={c.deltaTips} suffix="" /></td>}
                  <td className="num">
                    <ClickableValue query={{ ...qA, customer: c.customer }} title={c.customer + ' revenue — ' + data.periodLabel}>{moneyK(c.currRev)}</ClickableValue>
                  </td>
                  <td>{rateTag(c.rate)}</td>
                </tr>
              ))}
              {data.topCustomers.length === 0 && (
                <tr><td colSpan={isAvgMode ? 4 : 6} className="note">No customer activity in this period.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="panel">
          <h3>Pricing mix — {data.periodLabel}</h3>
          <table>
            <thead>
              <tr>
                <th>Regular rate</th>
                <th className="num">Loads</th>
                <th className="num">% loads</th>
                <th className="num">Revenue</th>
                <th className="num">Customers</th>
              </tr>
            </thead>
            <tbody>
              {data.pricingMix.map(b => {
                const p = A.tips > 0 ? (b.tips / A.tips) * 100 : 0;
                return (
                  <tr key={b.rate}>
                    <td>{rateTag(b.rate)}</td>
                    <td className="num">
                      <ClickableValue query={{ ...qA, rate: Math.round(b.rate) }} title={'Loads at $' + Math.round(b.rate) + ' — ' + data.periodLabel}>{num(b.tips)}</ClickableValue>
                    </td>
                    <td className="num">{p.toFixed(1)}%</td>
                    <td className="num">
                      <ClickableValue query={{ ...qA, rate: Math.round(b.rate) }} title={'Revenue at $' + Math.round(b.rate) + ' — ' + data.periodLabel}>{moneyK(b.revenue)}</ClickableValue>
                    </td>
                    <td className="num">{b.customers}</td>
                  </tr>
                );
              })}
              {data.pricingMix.length === 0 && (
                <tr><td colSpan="5" className="note">No activity in this period.</td></tr>
              )}
            </tbody>
          </table>
          <div className="note">Premium = $600+; Discount = $470 and below; Standard = everything else.</div>
        </div>
      </div>

      <div className="panel">
        <h3>Quarterly trend (last 3 fiscal years)</h3>
        <table>
          <thead>
            <tr>
              <th>Quarter</th>
              <th className="num">Loads</th>
              <th className="num">Volume m³</th>
              <th className="num">Working days*</th>
              <th className="num">Loads / working day*</th>
              <th className="num">Revenue</th>
              <th className="num">Rev / working day*</th>
            </tr>
          </thead>
          <tbody>
            {data.quarterly.map(q => (
              <tr key={q.label}>
                <td>{q.label}</td>
                <td className="num">
                  <ClickableValue query={{ fy: q.fy, q: q.q }} title={q.label}>{num(q.tips)}</ClickableValue>
                </td>
                <td className="num">
                  <ClickableValue query={{ fy: q.fy, q: q.q }} title={q.label}>{num(q.volumeM3)}</ClickableValue>
                </td>
                <td className="num">{num(q.workedDays)}</td>
                <td className="num"><strong>{q.tipsPerWorkingDay.toFixed(1)}</strong></td>
                <td className="num">{moneyK(q.revenue)}</td>
                <td className="num">{money(q.revPerWorkingDay)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="note">
          Q1 = Jun–Aug, Q2 = Sep–Nov, Q3 = Dec–Feb, Q4 = Mar–May.
          <strong> *Working day = Mon–Fri</strong> (5/week). Weekend loads still count toward Tips and Revenue but not toward the per-working-day denominator. In-progress quarters cap at today's date.
        </div>
      </div>

      <div className="small" style={{ marginTop: '0.6rem' }}>
        Revenue is estimated as loads × per-customer rate (from the SharePoint pricing sheet) for the load's tier.
        Volume is the tank size from the plate registry.
        <strong> *Working day = Mon–Fri (5 days per week)</strong>; weekend dates are excluded from the denominator. Any loads logged on a Saturday or Sunday still appear in the load count and revenue total.
        {isAvgMode && ' For day/week views, the comparison is an average per working day computed from Mon-Fri counts over the prior 365 days.'}
      </div>

      <CustomerAnalytics />
    </>
  );
}

function KpiCard({ lbl, val, valQuery, delta, vs, title }) {
  return (
    <div className="kpi">
      <div className="lbl">{lbl}</div>
      <div className="val">
        {valQuery
          ? <ClickableValue query={valQuery} title={title}>{val}</ClickableValue>
          : val}
      </div>
      <div className="delta">{delta} <span style={{ color: '#999' }}>{vs}</span></div>
    </div>
  );
}

// CustomerAnalytics — per-customer trend section.
// Customer dropdown → trailing 12 months vs prior 12 months.
// Three stat cards (avg monthly tips, avg monthly spend, avg rate)
// + a 12-row trend table comparing each month to the same month last year.
function CustomerAnalytics() {
  // Pull the customers list to derive distinct reporting groups. The
  // dropdown now lets you pick a rollup label (e.g. "Metrocon") rather
  // than a single billing entity, so the trend chart shows combined
  // history for every customer with that reporting_group. Backend
  // (/api/customer-analytics) already resolves either a customer name
  // OR a reporting group to the group and matches all members — added
  // in the schema-020 wiring.
  const { data: customersApi } = useApi('/api/customers');
  const [customer, setCustomer] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fit-to-one-page for the printed customer report (2026-10-22). When
  // the browser switches to print layout, measure the report; if it is
  // taller than one landscape letter page (8.5in = 816px), shrink it with
  // CSS zoom just enough to fit. Restored when printing ends.
  useEffect(() => {
    const mq = window.matchMedia('print');
    const PAGE_PX = 8.5 * 96 - 6;
    const fit = () => {
      const el = document.querySelector('.customer-report');
      if (!el) return;
      el.style.zoom = '';
      if (!mq.matches) return;
      const h = el.scrollHeight;
      if (h > PAGE_PX) el.style.zoom = String(Math.floor((PAGE_PX / h) * 100) / 100);
    };
    const reset = () => { const el = document.querySelector('.customer-report'); if (el) el.style.zoom = ''; };
    mq.addEventListener ? mq.addEventListener('change', fit) : mq.addListener(fit);
    window.addEventListener('afterprint', reset);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', fit) : mq.removeListener(fit);
      window.removeEventListener('afterprint', reset);
    };
  }, []);

  // Distinct reporting groups from the customers table, sorted.
  // Falls back to display names if reporting_group is empty for any row
  // (shouldn't happen post-schema-020 backfill but defensive).
  const customerRows = (customersApi && customersApi.customers) || [];
  const customerOptions = Array.from(new Set(
    customerRows
      .map(c => (c.reporting_group || c.display_name || '').trim())
      .filter(Boolean)
  )).sort((a, b) => a.localeCompare(b));

  useEffect(() => {
    if (!customer) { setData(null); return; }
    setLoading(true);
    setError(null);
    fetch('/api/customer-analytics?customer=' + encodeURIComponent(customer), { credentials: 'include' })
      .then(r => { if (!r.ok) throw new Error(r.status + ' ' + r.statusText); return r.json(); })
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [customer]);

  return (
    <div className="panel" style={{ marginTop: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <h3 style={{ margin: 0 }}>Customer analytics</h3>
        <select value={customer} onChange={e => setCustomer(e.target.value)}
                className="refresh-btn" style={{ minWidth: 260 }}>
          <option value="">— pick a reporting group —</option>
          {customerOptions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {loading && <span className="small" style={{ color: '#666' }}>loading…</span>}
        {error && <span className="small" style={{ color: '#991b1b' }}>error: {error}</span>}
      </div>
      {!customer && (
        <div className="note">
          Pick a reporting group to see trailing-12-month performance and a YoY comparison.
          Rollups combine every customer whose reporting_group matches — set these in Reference → Customers.
        </div>
      )}
      {data && data.ok && (() => {
        const cur = data.averages.current;
        const pri = data.averages.prior;
        const d   = data.averages.delta;
        // data.comparison (avg-customer / multiplier / share) still
        // computed by the backend but not shown — Suresh removed the
        // "vs. average customer" panel 2026-07-07 because a mean is
        // easily skewed by a single very large customer and the intent
        // is a report for a general audience (not statisticians).
        const patt  = data.patterns || {};
        const rates = (data.billing && data.billing.rates) || {};
        const contacts = data.contacts || [];
        // Customer-facing wording (2026-10-22, Suresh sends this report to
        // the customer): no internal names, notes or "generated" stamps in
        // print. Staff-only details stay on screen in .no-print lines.
        const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        // Accepts 'YYYY-MM-DD' (-> "Nov 1, 2025") or 'YYYY-MM' (-> "Nov 2025")
        const niceDate = (iso) => {
          const m = String(iso || '').match(/^(\d{4})-(\d{2})(?:-(\d{2}))?/);
          if (!m) return String(iso || '');
          return m[3] ? MON[+m[2] - 1] + ' ' + (+m[3]) + ', ' + m[1] : MON[+m[2] - 1] + ' ' + m[1];
        };
        const niceMonth = (iso) => {
          const m = String(iso || '').match(/^(\d{4})-(\d{2})/);
          return m ? MON[+m[2] - 1] + ' ' + m[1] : '';
        };
        return (
          <div className="customer-report" data-print-scope>
            <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.4rem' }}>
              <button className="refresh-btn" onClick={() => window.print()}
                      style={{ background: '#0a8b3a', color: '#fff' }}>
                🖨 Print report
              </button>
            </div>

            {/* Branded letterhead (2026-10-22): logo left, report title
                + customer + period right, green rule underneath. */}
            <div className="report-letterhead">
              <img src="/ecovac-logo.png" alt="EcoVac" className="report-logo" />
              <div className="report-title-block">
                <div className="report-kicker">Customer Activity Report</div>
                <div className="report-customer">{data.customer}</div>
                <div className="report-period">
                  {niceDate(data.windows.current.from)} – {niceDate(data.windows.current.to)}
                  {data.firstSeen && niceMonth(data.firstSeen) && <> &nbsp;·&nbsp; Customer since {niceMonth(data.firstSeen)}</>}
                </div>
              </div>
            </div>

            {/* Row 1: headline KPIs with YoY delta */}
            <div className="kpi-grid" style={{ marginBottom: '0.75rem' }}>
              <KpiCard lbl="Total loads (12mo)"
                val={num(data.windows.current.tips)}
                delta={<Delta value={pct12(data.windows.current.tips, data.windows.prior.tips)} />}
                vs={<>vs {num(data.windows.prior.tips)} prior 12mo</>}
                title={data.customer + ' — total loads'} />
              <KpiCard lbl="Total revenue (12mo)"
                val={moneyK(data.windows.current.revenue)}
                delta={<Delta value={pct12(data.windows.current.revenue, data.windows.prior.revenue)} />}
                vs={<>vs {moneyK(data.windows.prior.revenue)} prior 12mo</>}
                title={data.customer + ' — total revenue'} />
              <KpiCard lbl="Total volume (m³)"
                val={num(Math.round(data.windows.current.volumeM3 || 0))}
                delta={<Delta value={pct12(data.windows.current.volumeM3, data.windows.prior.volumeM3)} />}
                vs={<>vs {num(Math.round(data.windows.prior.volumeM3 || 0))} m³ prior 12mo</>}
                title={data.customer + ' — total volume'} />
              <KpiCard lbl="Avg rate / load"
                val={money(cur.avgRate)}
                delta={<Delta value={d.avgRate} />}
                vs={<>vs {money(pri.avgRate)} prior 12mo</>}
                title={data.customer + ' — avg rate/load'} />
            </div>

            {/* Row 2: cadence — monthly / weekly / daily averages */}
            <div className="kpi-grid" style={{ marginBottom: '0.75rem' }}>
              <KpiCard lbl="Loads / month"
                val={cur.tipsPerMonth.toFixed(1)}
                delta={<Delta value={d.tipsPerMonth} />}
                vs={<>vs {pri.tipsPerMonth.toFixed(1)} prior yr</>}
                title="Loads per month, last 12 mo" />
              <KpiCard lbl="Loads / week"
                val={cur.tipsPerWeek.toFixed(1)}
                delta={null}
                vs={<>~{cur.tipsPerDay.toFixed(2)} loads / day (calendar)</>}
                title="Cadence — weekly" />
              <KpiCard lbl="Revenue / month"
                val={moneyK(cur.revenuePerMonth)}
                delta={<Delta value={d.revenuePerMonth} />}
                vs={<>vs {moneyK(pri.revenuePerMonth)} prior yr</>}
                title="Revenue per month, last 12 mo" />
              <KpiCard lbl="Avg volume / load"
                val={cur.avgVolumePerLoad.toFixed(1) + ' m³'}
                delta={null}
                vs={<>{cur.volumePerMonth.toFixed(0)} m³ / month avg</>}
                title="Load-size cadence" />
            </div>

            {(() => {
              // Chart data: axis label shows "Aug 2025" style (short
              // month + full year) so a printed report is legible
              // without needing a legend key. Tooltip still shows the
              // exact YYYY-MM for both current + prior series.
              const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                              'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              const monthLabel = (ym) => {
                const m = String(ym || '').match(/^(\d{4})-(\d{2})$/);
                if (!m) return String(ym || '');
                const y = m[1];
                const mi = parseInt(m[2], 10) - 1;
                return (MONTHS[mi] || m[2]) + ' ' + y;
              };
              const chartData = data.monthly.map(m => ({
                month:       monthLabel(m.month),  // "Aug 2025"
                fullMonth:   m.month,
                priorMonth:  m.priorMonth,
                loads:       m.tips,
                priorLoads:  m.priorTips,
                revenue:     m.revenue,
                priorRevenue: m.priorRevenue
              }));
              return (
                // Revenue chart was here (side-by-side with loads).
                // Removed 2026-07-07 — Suresh: revenue tracks loads 1:1
                // for any single customer since the per-customer rate is
                // constant, so the second chart is visual noise. Screen
                // + print both show just the loads chart now, full-width.
                //
                // Wrapper div with explicit width so ResponsiveContainer
                // (which uses ResizeObserver on its parent) has a real
                // box to measure. Without the wrapper the chart rendered
                // as two giant legend circles because the container
                // computed width=0.
                // One chart for screen AND print (2026-10-22, Suresh: the
                // customer gets this report, so it must look clean). The
                // same SVG is shown on screen and in the PDF, so what you
                // see is what the customer gets. Smooth curves, a value
                // callout on every point.
                <div className="report-charts-row" style={{ width: '100%', display: 'block' }}>
                  <div className="chart-card" style={{ padding: '0.6rem 0.85rem', width: '100%' }}>
                    <h4 style={{ margin: '0 0 0.15rem 0', fontSize: '0.95em' }}>Loads — month over month</h4>
                    <LoadsTrendChart data={chartData} />
                  </div>
                </div>
              );
            })()}
            <div className="note no-print" style={{ marginTop: '0.5rem' }}>
              <strong>Green</strong> = last 12 months. <strong>Grey</strong> = the same month one year earlier (shown when there is history).
            </div>

            {/* Row 4: patterns — day of week + hour of day + tier mix */}
            <div className="report-patterns" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginTop: '0.75rem' }}>
              <div className="chart-card" style={{ padding: '0.5rem 0.75rem' }}>
                <h4 style={{ margin: '0 0 0.4rem 0', fontSize: '0.95em' }}>Day of week</h4>
                <BarBlock rows={(patt.dayOfWeek || []).map(x => ({ label: x.day, value: x.tips }))} />
              </div>
              <div className="chart-card" style={{ padding: '0.5rem 0.75rem' }}>
                <h4 style={{ margin: '0 0 0.4rem 0', fontSize: '0.95em' }}>Hour of day</h4>
                <BarBlock rows={(patt.hourBuckets || []).filter(x => x.bucket !== 'unknown').map(x => ({ label: x.bucket, value: x.tips }))} />
              </div>
              <div className="chart-card" style={{ padding: '0.5rem 0.75rem' }}>
                <h4 style={{ margin: '0 0 0.4rem 0', fontSize: '0.95em' }}>Tank size mix</h4>
                <BarBlock rows={[
                  { label: 'Small',        value: (patt.tierMix && patt.tierMix.small) || 0 },
                  { label: 'Regular',      value: (patt.tierMix && patt.tierMix.regular) || 0 },
                  { label: 'Oversize 18m³', value: (patt.tierMix && patt.tierMix.oversize18) || 0 },
                  { label: 'Oversize 28m³', value: (patt.tierMix && patt.tierMix.oversize28) || 0 }
                ]} />
                {patt.drillMud && (
                  <div className="small" style={{ marginTop: '0.35rem', color: '#64748b' }}>
                    Drill mud: <strong>{patt.drillMud.yes || 0}</strong>{' '}of{' '}
                    {(patt.drillMud.yes || 0) + (patt.drillMud.no || 0)} loads
                  </div>
                )}
              </div>
            </div>

            {/* Row 5: billing rates (full width) */}
            <div className={'chart-card' + (data.billing ? '' : ' no-print')} style={{ padding: '0.5rem 0.75rem', marginTop: '0.75rem' }}>
              <h4 style={{ margin: '0 0 0.4rem 0', fontSize: '0.95em' }}>Your rates</h4>
              {data.billing ? (
                <>
                  {(() => {
                    // Only rates that are set (no "—" cells on a customer copy)
                    const cells = [
                      ['Small', rates.small], ['Regular', rates.regular],
                      ['Oversize 18m³', rates.oversize18], ['Oversize 28m³', rates.oversize28],
                      ['Drill mud', rates.drillMud], ['Drill mud 18m³', rates.drillMud18],
                      ['Drill mud 28m³', rates.drillMud28]
                    ].filter(([, v]) => v != null);
                    return (
                      <div className="report-rates" style={{ display: 'grid', gridTemplateColumns: 'repeat(' + Math.max(1, cells.length) + ', 1fr)', gap: '0.35rem', fontSize: '0.85em' }}>
                        {cells.map(([l, v]) => <RateCell key={l} label={l} val={v} />)}
                      </div>
                    );
                  })()}                  {/* Staff only — never printed / sent to the customer */}
                  <div className="small no-print" style={{ color: '#94a3b8', marginTop: '0.5rem' }}>
                    Internal (not printed): QBO name <strong>{data.billing.qboFullName || data.billing.displayName}</strong>
                    {data.billing.billedTo && <> · Billed to <strong>{data.billing.billedTo}</strong></>}
                    {' '}· Reporting group <strong>{data.billing.reportingGroup}</strong>
                    {data.billing.notes && <> · Notes: <em>{data.billing.notes}</em></>}
                  </div>
                </>
              ) : (
                <div className="small" style={{ color: '#94a3b8' }}>Not linked to a customers-table row — no rate card on file.</div>
              )}
            </div>

            {/* Footer: EcoVac contact details (same as the statement PDFs) */}
            <div className="report-footer">
              <div><strong>EcoVac Solutions Ltd.</strong> &nbsp;·&nbsp; Head Office: 58 &amp; 60A Baywood Road, Etobicoke, ON M9V 3Y8</div>
              <div>Phone: 647 547 1667 &nbsp;·&nbsp; Email: info@ecovac.ca &nbsp;·&nbsp; www.ecovac.ca</div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// One tile in the "vs. average customer" band. thisVal + avgVal are
// already display-formatted strings. mult is a numeric multiplier
// (e.g. 2.4 = 2.4x). share is a percentage 0-100 or null.
function VsAvgTile({ label, thisVal, avgVal, mult, share }) {
  const multStr = mult == null || !isFinite(mult) ? '—'
                : mult >= 100 ? '100x+'
                : mult.toFixed(mult >= 10 ? 0 : 1) + 'x';
  const multColor = mult == null ? '#94a3b8'
                  : mult >= 1.5 ? '#166534'
                  : mult <= 0.7 ? '#b91c1c'
                  : '#334155';
  return (
    <div style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '0.4rem 0.5rem' }}>
      <div className="small" style={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{label}</div>
      <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', marginTop: '0.15rem' }}>
        {thisVal}
      </div>
      <div className="small" style={{ color: '#64748b' }}>
        avg: <strong>{avgVal}</strong>
      </div>
      <div style={{ fontSize: '0.85rem', fontWeight: 700, color: multColor, marginTop: '0.15rem' }}>
        {multStr}
        {share != null && (
          <span className="small" style={{ marginLeft: '0.4rem', fontWeight: 500, color: '#64748b' }}>
            {share.toFixed(1)}% of total
          </span>
        )}
      </div>
    </div>
  );
}

// Compact horizontal bar chart for pattern breakdowns. Renders each row
// as a labelled bar with the count on the right.
function BarBlock({ rows }) {
  const max = Math.max(1, ...rows.map(r => Number(r.value) || 0));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
      {rows.map((r, i) => {
        const w = ((Number(r.value) || 0) / max) * 100;
        return (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '6.5em 1fr 3em', gap: '0.35rem', alignItems: 'center' }}>
            <div className="small" style={{ color: '#334155' }}>{r.label}</div>
            <div style={{ background: '#e2e8f0', borderRadius: 3, height: '0.55rem', overflow: 'hidden' }}>
              <div style={{ background: '#0a8b3a', height: '100%', width: w + '%', borderRadius: 3 }} />
            </div>
            <div className="small" style={{ color: '#0f172a', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
              {Number(r.value || 0).toLocaleString()}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// One cell in the rate card. Amber flag for drill-mud rates.
function RateCell({ label, val }) {
  const shown = val == null ? '—' : '$' + Number(val).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <div className="rate-cell">
      <div className="rate-label">{label}</div>
      <div className="rate-value">{shown}</div>
    </div>
  );
}

// Helper: year-over-year percent delta.
function pct12(a, b) {
  if (!b) return null;
  return ((a - b) / b) * 100;
}

// Extract 'YYYY-MM-DD' from either a Date-ish string or Date object. The
// Overview API returns period boundaries as JSON-serialized Dates (ISO
// strings); .slice(0,10) is the safe extraction.
function isoOf(d) {
  if (!d) return '';
  const s = String(d);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

// SoilFlo tacks a unit number onto hauler strings ('365 DOSS #1979').
// Strip that so aggregation groups by trucking COMPANY, not truck-unit.
function normalizeHaulerOverview(raw) {
  let s = String(raw || '').trim();
  s = s.replace(/\s*[-\s]*#.*$/, '').trim();
  return s || '(unknown)';
}

// One pass over outgoing-load rows: totalLoads + distinct trucking
// companies (post-normalization). Used by the top KPI tile so it and
// the OutgoingLoadsAnalytics panel share the same data source.
function computeOutgoingTotals(rows) {
  const haulers = new Set();
  let totalLoads = 0;
  for (const r of (rows || [])) {
    const hRaw = r.hauler || pickRawOverview(r, ['Hauler']) || '';
    haulers.add(normalizeHaulerOverview(hRaw));
    totalLoads += 1;
  }
  return { totalLoads, haulerCount: haulers.size };
}

// Match the pickRaw helper in OutgoingLoads.jsx — try each header key,
// case + whitespace insensitive, return first non-empty raw value.
function pickRawOverview(row, keys) {
  const raw = row && row.raw;
  if (!raw || typeof raw !== 'object') return '';
  for (const k of keys) {
    for (const rk of Object.keys(raw)) {
      if (rk === k || rk.trim().toLowerCase() === String(k).trim().toLowerCase()) {
        const v = raw[rk];
        if (v != null && String(v).trim() !== '') return v;
      }
    }
  }
  return '';
}

// OutgoingLoadsAnalytics — mirrors the CustomerAnalytics pattern.
// Fetches every outgoing load in the current Overview period, aggregates
// by trucking company (hauler), shows total-loads + net-weight breakdown.
// Client-side aggregation is fine here — Outgoing Loads table caps at
// 5000 rows and periods are short enough that this stays well under.
function OutgoingLoadsAnalytics({ from, to, periodLabel, onDrill }) {
  const qs = new URLSearchParams({ limit: '5000' });
  if (from) qs.set('from', from);
  if (to)   qs.set('to',   to);
  const { data, loading, error, refresh } = useApi('/api/loads-outgoing?' + qs.toString());
  // Vendor lookup for the "By waste type" rollup. If the vendors table
  // isn't yet applied (schema-021 missing), we render every load as
  // Unclassified — degrades gracefully instead of breaking the whole
  // panel.
  const vendorsApi = useApi('/api/vendors');
  const vendorByCanon = new Map();
  for (const v of ((vendorsApi.data && vendorsApi.data.vendors) || [])) {
    if (v.canonical_name) vendorByCanon.set(v.canonical_name, v);
  }

  const rows = (data && data.rows) || [];

  // Normalize hauler string. SoilFlo formats hauler names as
  //   '<Trucking Company> #<Unit>' — e.g. '365 DOSS #1979', 'TD Trucking #373'.
  // For aggregation we want to roll up by COMPANY, not by truck-unit, so
  // strip the trailing '#<digits>' plus any spaces before it. Case-preserved.
  //   '365 DOSS #1979'    -> '365 DOSS'
  //   'TD Trucking #373'  -> 'TD Trucking'
  //   'Pradhi #9947'      -> 'Pradhi'
  function normalizeHauler(raw) {
    let s = String(raw || '').trim();
    // Strip everything from '#' on: units come as '#1979', '#22-03', '#VP-16'.
    s = s.replace(/\s*[-\s]*#.*$/, '').trim();
    return s || '(unknown)';
  }

  // Aggregate — one pass, group by NORMALIZED hauler company, sum loads + net weight.
  // Net weight comes from the SoilFlo raw column; MT is the standard unit
  // in this dataset, so we ignore the unit for aggregation (all rows are MT).
  const byHauler = new Map();
  let totalLoads = 0, totalWeight = 0;
  for (const r of rows) {
    const rawHauler = r.hauler || pickRawOverview(r, ['Hauler']) || '';
    const hauler = normalizeHauler(rawHauler);
    const wRaw = pickRawOverview(r, ['Net Weight', 'Net Weight (Scale)']);
    const w = wRaw === '' ? 0 : Number(wRaw);
    const wt = Number.isFinite(w) ? w : 0;
    const agg = byHauler.get(hauler) || { loads: 0, weight: 0, cost: 0, costed: 0 };
    agg.loads += 1;
    agg.weight += wt;
    // Haulage component from the rate charts (schema-049) — rides on
    // every /api/loads-outgoing row. null = no rate; excluded from avg.
    if (r.haulage_cost != null) { agg.cost += Number(r.haulage_cost) || 0; agg.costed += 1; }
    byHauler.set(hauler, agg);
    totalLoads += 1;
    totalWeight += wt;
  }
  const haulers = Array.from(byHauler.entries())
    .map(([hauler, v]) => ({ hauler, loads: v.loads, weight: v.weight, cost: v.cost, costed: v.costed,
                             avgCost: v.costed > 0 ? v.cost / v.costed : null,
                             pctLoads: totalLoads > 0 ? (v.loads / totalLoads) * 100 : 0,
                             pctWeight: totalWeight > 0 ? (v.weight / totalWeight) * 100 : 0 }))
    .sort((a, b) => b.loads - a.loads);
  // Grand totals for the section header: full cost (haulage + tip).
  let grandCost = 0, grandCosted = 0;
  for (const r of rows) {
    if (r.total_cost != null) { grandCost += Number(r.total_cost) || 0; grandCosted += 1; }
  }

  // Second aggregation — by receiving site (destination). Same rows, but
  // counting loads + summing Volume (m³) rather than net weight. Volume
  // comes off the typed `quantity` column when present, falling back to
  // the raw `Volume` column from the SoilFlo export.
  const byDest = new Map();
  let totalVolume = 0;
  for (const r of rows) {
    const dest = (r.destination || pickRawOverview(r, ['Receiving Site']) || '(unknown)').trim() || '(unknown)';
    // node-pg returns NUMERIC columns as STRINGS to preserve precision, so
    // r.quantity is e.g. "27.000" not 27. Number.isFinite("27.000") is false
    // (strict number check), which quietly zeroed every row before. Coerce
    // via Number() first, then guard.
    let v = r.quantity == null ? null : Number(r.quantity);
    if (v == null || !Number.isFinite(v)) {
      const vRaw = pickRawOverview(r, ['Volume']);
      v = vRaw === '' ? 0 : Number(vRaw);
    }
    const vol = Number.isFinite(v) ? v : 0;
    const agg = byDest.get(dest) || { loads: 0, volume: 0, cost: 0, costed: 0 };
    agg.loads += 1;
    agg.volume += vol;
    if (r.tip_fee != null) { agg.cost += Number(r.tip_fee) || 0; agg.costed += 1; }
    byDest.set(dest, agg);
    totalVolume += vol;
  }
  const destinations = Array.from(byDest.entries())
    .map(([dest, v]) => ({ dest, loads: v.loads, volume: v.volume, cost: v.cost, costed: v.costed,
                           avgCost: v.costed > 0 ? v.cost / v.costed : null,
                           pctLoads: totalLoads > 0 ? (v.loads / totalLoads) * 100 : 0,
                           pctVolume: totalVolume > 0 ? (v.volume / totalVolume) * 100 : 0 }))
    .sort((a, b) => b.loads - a.loads);

  // Third aggregation — by waste type. Uses the vendors table to map
  // destination -> waste_type; only dump_site / both vendors contribute
  // (haulers don't have a meaningful waste_type). Anything without a
  // matching vendor row (or where vendor.waste_type is blank) rolls up
  // under "Unclassified". Also collects the set of destinations feeding
  // each bucket so the drilldown modal can filter by them.
  const byWaste = new Map();
  for (const [dest, v] of byDest.entries()) {
    const canon = String(dest).trim().toLowerCase();
    const vendor = vendorByCanon.get(canon);
    const isDumpish = vendor && (vendor.vendor_type === 'dump_site' || vendor.vendor_type === 'both' || vendor.vendor_type == null);
    const wt = (isDumpish && vendor.waste_type && String(vendor.waste_type).trim()) || 'Unclassified';
    const agg = byWaste.get(wt) || { loads: 0, volume: 0, destinations: [], cost: 0, costed: 0 };
    agg.loads += v.loads;
    agg.volume += v.volume;
    agg.cost += v.cost || 0;
    agg.costed += v.costed || 0;
    agg.destinations.push(dest);
    byWaste.set(wt, agg);
  }
  const wasteTypes = Array.from(byWaste.entries())
    .map(([wt, v]) => ({ wt, loads: v.loads, volume: v.volume, destinations: v.destinations,
                          cost: v.cost, avgCost: v.costed > 0 ? v.cost / v.costed : null,
                          pctLoads: totalLoads > 0 ? (v.loads / totalLoads) * 100 : 0,
                          pctVolume: totalVolume > 0 ? (v.volume / totalVolume) * 100 : 0 }))
    .sort((a, b) => {
      // Unclassified always last, otherwise by loads desc.
      if (a.wt === 'Unclassified' && b.wt !== 'Unclassified') return 1;
      if (b.wt === 'Unclassified' && a.wt !== 'Unclassified') return -1;
      return b.loads - a.loads;
    });

  return (
    <div className="panel" style={{ marginTop: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.4rem' }}>
        <h3 style={{ margin: 0 }}>Outgoing loads — {periodLabel}
        {grandCosted > 0 && (
          <span style={{ fontSize: '0.8em', fontWeight: 600, color: '#0f766e', marginLeft: '0.6rem' }}>
            total cost ${Math.round(grandCost).toLocaleString()} · avg ${Math.round(grandCost / grandCosted).toLocaleString()}/load
            {grandCosted < rows.length ? ' · ' + (rows.length - grandCosted) + ' unrated' : ''}
          </span>
        )}
      </h3>
        <div className="small" style={{ color: '#666' }}>
          {from && to ? (from + ' → ' + to) : 'no date range'} · <button className="link" onClick={refresh} style={{ color: '#0369a1' }}>reload</button>
        </div>
      </div>

      {loading && <div className="small" style={{ color: '#888' }}>Loading outgoing loads…</div>}
      {error && <div className="error small">Error: {error}</div>}

      {!loading && !error && (
        <>
          {/*
            Top rollup tiles (loads out + net weight) were moved: loads-out
            now lives in the Business Snapshot KPI grid at the top of the
            Overview tab; net-weight was dropped per Suresh 2026-07-07 as
            not useful at that level. What's left in this section is the
            per-hauler and per-destination breakdown, which stays here.
          */}
          {/* By waste type — highest-level view: what's leaving the plant.
              Uses vendors.waste_type to map destinations to a category.
              Loads whose destination isn't in the vendors table (or where
              the vendor has no waste_type set) roll up under "Unclassified"
              with a link to Reference → Vendors. */}
          {wasteTypes.length > 0 && (
            <>
              <h4 style={{ margin: '0.4rem 0 0.4rem 0' }}>By waste type</h4>
              <div style={{ display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                            gap: '0.5rem',
                            marginBottom: '0.75rem' }}>
                {wasteTypes.map(w => {
                  const isUnclassified = w.wt === 'Unclassified';
                  return (
                    <div key={w.wt} className="kpi"
                         onClick={() => onDrill && onDrill({
                           title: w.wt + ' — ' + periodLabel,
                           filter: { destinations: w.destinations }
                         })}
                         style={{
                           background: isUnclassified ? '#fff7ed' : '#eef2ff',
                           border: '1px solid ' + (isUnclassified ? '#fdba74' : '#818cf8'),
                           boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
                           cursor: onDrill ? 'pointer' : 'default'
                         }}
                         title={'Click to see loads · ' + w.wt + ' · ' + w.loads + ' loads · ' + w.volume.toFixed(1) + ' m³ · ' + w.destinations.length + ' destination' + (w.destinations.length === 1 ? '' : 's')}>
                      <div className="lbl" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                     color: isUnclassified ? '#9a3412' : '#3730a3' }}>
                        {w.wt}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: '0.25rem', gap: '0.5rem' }}>
                        <div>
                          <div className="val" style={{ fontSize: '1.2rem', lineHeight: 1 }}>{w.loads.toLocaleString()}</div>
                          <div className="small" style={{ color: '#64748b' }}>loads · {w.pctLoads.toFixed(1)}%</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div className="val" style={{ fontSize: '1.05rem', lineHeight: 1, color: '#334155' }}>
                            {w.volume.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                            <span style={{ fontSize: '0.65em', color: '#888', marginLeft: 2 }}>m³</span>
                          </div>
                          <div className="small" style={{ color: '#64748b' }}>{w.destinations.length} dest.</div>
                        </div>
                      </div>
                      <div className="small" style={{ marginTop: '0.3rem', color: '#334155', fontWeight: 600 }}>
                        {w.avgCost != null
                          ? '$' + Math.round(w.cost).toLocaleString() + ' tip fees · avg $' + Math.round(w.avgCost).toLocaleString() + '/load'
                          : ''}
                      </div>
                      {isUnclassified && (
                        <div className="small" style={{ marginTop: '0.3rem', color: '#9a3412' }}>
                          Categorize in Reference → Vendors
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {haulers.length === 0 ? (
            <div className="note" style={{ padding: '0.5rem 0.7rem', background: '#f8fafc', borderRadius: 4 }}>
              No outgoing loads in this period.
            </div>
          ) : (
            <>
              <h4 style={{ margin: '0.4rem 0 0.4rem 0' }}>By trucking company</h4>
              <div style={{ display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                            gap: '0.5rem' }}>
                {haulers.map(h => (
                <div key={h.hauler} className="kpi"
                     onClick={() => onDrill && onDrill({ title: h.hauler + ' — ' + periodLabel, filter: { hauler: h.hauler } })}
                     style={{ background: '#f8fafc', border: '1px solid #94a3b8', boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
                              cursor: onDrill ? 'pointer' : 'default' }}
                     title={'Click to see loads · ' + h.hauler + ' · ' + h.loads + ' loads · ' + h.weight.toFixed(2) + ' MT'}>
                  <div className="lbl" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {h.hauler}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: '0.25rem', gap: '0.5rem' }}>
                    <div>
                      <div className="val" style={{ fontSize: '1.2rem', lineHeight: 1 }}>{h.loads.toLocaleString()}</div>
                      <div className="small" style={{ color: '#64748b' }}>loads · {h.pctLoads.toFixed(1)}%</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div className="val" style={{ fontSize: '1.05rem', lineHeight: 1, color: '#334155' }}>
                        {h.weight.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                        <span style={{ fontSize: '0.65em', color: '#888', marginLeft: 2 }}>MT</span>
                      </div>
                      <div className="small" style={{ color: '#64748b' }}>{h.pctWeight.toFixed(1)}%</div>
                    </div>
                  </div>
                  <div className="small" style={{ marginTop: '0.3rem', color: '#0f766e', fontWeight: 600 }}>
                    {h.costed > 0
                      ? '$' + Math.round(h.cost).toLocaleString() + ' haulage · avg $' + Math.round(h.avgCost).toLocaleString() + '/load'
                      : 'no haulage rate on chart'}
                    {h.costed > 0 && h.costed < h.loads ? ' · ' + (h.loads - h.costed) + ' unrated' : ''}
                  </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* By destination — loads count + volume in m³ per receiving site. */}
          {destinations.length > 0 && (
            <>
              <h4 style={{ margin: '1rem 0 0.4rem 0' }}>By destination</h4>
              <div style={{ display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                            gap: '0.5rem' }}>
                {destinations.map(d => (
                  <div key={d.dest} className="kpi"
                       onClick={() => onDrill && onDrill({ title: d.dest + ' — ' + periodLabel, filter: { destination: d.dest } })}
                       style={{ background: '#f8fafc', border: '1px solid #94a3b8', boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
                                cursor: onDrill ? 'pointer' : 'default' }}
                       title={'Click to see loads · ' + d.dest + ' · ' + d.loads + ' loads · ' + d.volume.toFixed(1) + ' m³'}>
                    <div className="lbl" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {d.dest}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: '0.25rem', gap: '0.5rem' }}>
                      <div>
                        <div className="val" style={{ fontSize: '1.2rem', lineHeight: 1 }}>{d.loads.toLocaleString()}</div>
                        <div className="small" style={{ color: '#64748b' }}>loads · {d.pctLoads.toFixed(1)}%</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div className="val" style={{ fontSize: '1.05rem', lineHeight: 1, color: '#334155' }}>
                          {d.volume.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                          <span style={{ fontSize: '0.65em', color: '#888', marginLeft: 2 }}>m³</span>
                        </div>
                        <div className="small" style={{ color: '#64748b' }}>{d.pctVolume.toFixed(1)}%</div>
                      </div>
                    </div>
                    <div className="small" style={{ marginTop: '0.3rem', color: '#7c3aed', fontWeight: 600 }}>
                      {d.costed > 0
                        ? '$' + Math.round(d.cost).toLocaleString() + ' tip fees · avg $' + Math.round(d.avgCost).toLocaleString() + '/load'
                        : 'no tip rate on chart'}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="note" style={{ marginTop: '0.6rem' }}>
            Aggregated from the outgoing-loads table for the current Overview period.
            Trucking-company names normalized by stripping the '#&lt;truck-unit&gt;' suffix
            SoilFlo appends (so '365 DOSS #1979' rolls up with '365 DOSS #1998').
            Net weight assumes MT for all rows (the SoilFlo default); volume uses the
            typed quantity column with a fallback to the raw <code>Volume</code> column (m³).
            Rows without a value count 0 toward the sum but still count as a load.
            Click any tile to see the underlying loads.
          </div>
        </>
      )}
    </div>
  );
}

// OutgoingDrillDown — modal showing the raw outgoing loads behind a
// clicked tile. Reuses the .dd-* modal classes from styles.css so it
// visually matches the incoming DrillDown. Filters rows client-side
// from the already-fetched Overview outgoing set — no extra API call.
function OutgoingDrillDown({ rows, title, filter, from, to, onClose }) {
  const destSet = filter && Array.isArray(filter.destinations)
    ? new Set(filter.destinations.map(d => String(d).trim() || '(unknown)'))
    : null;
  const filtered = (rows || []).filter(r => {
    if (filter && filter.hauler) {
      const h = normalizeHaulerOverview(r.hauler || pickRawOverview(r, ['Hauler']) || '');
      if (h !== filter.hauler) return false;
    }
    if (filter && filter.destination) {
      const dRaw = (r.destination || pickRawOverview(r, ['Receiving Site']) || '(unknown)');
      const d = String(dRaw).trim() || '(unknown)';
      if (d !== filter.destination) return false;
    }
    if (destSet) {
      const dRaw = (r.destination || pickRawOverview(r, ['Receiving Site']) || '(unknown)');
      const d = String(dRaw).trim() || '(unknown)';
      if (!destSet.has(d)) return false;
    }
    return true;
  });

  // Sort by date DESC, then time DESC so the newest loads are at the top.
  const sorted = filtered.slice().sort((a, b) => {
    const ad = String(a.date || '');
    const bd = String(b.date || '');
    if (ad !== bd) return ad < bd ? 1 : -1;
    const at = String(a.time_at || '');
    const bt = String(b.time_at || '');
    return at < bt ? 1 : at > bt ? -1 : 0;
  });

  const totalWeight = sorted.reduce((s, r) => {
    const w = Number(pickRawOverview(r, ['Net Weight', 'Net Weight (Scale)']) || 0);
    return s + (Number.isFinite(w) ? w : 0);
  }, 0);
  const totalVolume = sorted.reduce((s, r) => {
    let v = r.quantity == null ? null : Number(r.quantity);
    if (v == null || !Number.isFinite(v)) {
      const vRaw = pickRawOverview(r, ['Volume']);
      v = vRaw === '' ? 0 : Number(vRaw);
    }
    return s + (Number.isFinite(v) ? v : 0);
  }, 0);

  return (
    <div className="dd-backdrop" onClick={onClose}>
      <div className="dd-modal" onClick={e => e.stopPropagation()}>
        <div className="dd-header">
          <div>
            <h3 style={{ margin: 0 }}>{title}</h3>
            <div className="small" style={{ color: '#64748b', marginTop: '0.15rem' }}>
              {from} → {to} · <strong>{sorted.length}</strong> load{sorted.length === 1 ? '' : 's'}
              {totalWeight > 0 && <> · <strong>{totalWeight.toLocaleString(undefined, { maximumFractionDigits: 1 })}</strong> MT</>}
              {totalVolume > 0 && <> · <strong>{totalVolume.toLocaleString(undefined, { maximumFractionDigits: 1 })}</strong> m³</>}
            </div>
          </div>
          <button className="refresh-btn" onClick={onClose}>Close</button>
        </div>
        <div className="dd-body">
          {sorted.length === 0 ? (
            <div className="note">No loads match this filter.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Hauler</th>
                  <th>Truck</th>
                  <th>Material</th>
                  <th className="num">Qty</th>
                  <th>Unit</th>
                  <th>Source site</th>
                  <th>Destination</th>
                  <th>Driver</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(r => (
                  <tr key={r.id}>
                    <td>{isoOf(r.date) || '—'}</td>
                    <td>{r.time_at || '—'}</td>
                    <td>{r.hauler || '—'}</td>
                    <td>{r.truck || '—'}</td>
                    <td>{r.material || '—'}</td>
                    <td className="num">{r.quantity == null || r.quantity === '' ? '—' : Number(r.quantity).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    <td>{r.unit || '—'}</td>
                    <td>{r.source_site || '—'}</td>
                    <td>{r.destination || '—'}</td>
                    <td>{r.driver || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
